/**
 * Shared logic for exporting a published assignment into a draft in another class.
 * AMCQ drafts must store `questions` as an array (CreateAMCQ / preview).
 * SAQ / OE drafts store `questions` as an object keyed by questionId (CreateSAQ).
 */
import {
  arrayUnion,
  doc,
  getDoc,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';

function exportFormatSuffix(assignmentData, assignmentId) {
  if (assignmentData?.format) return String(assignmentData.format);
  if (assignmentId && assignmentId.includes('+')) {
    return assignmentId.split('+').pop();
  }
  return 'OE';
}

/**
 * @returns {Array<object>} questions suitable for AMCQ create / preview
 */
export function normalizeAmcqQuestionsForExport(rawQuestions, amcqQuestionsFlat) {
  let list = [];
  if (Array.isArray(rawQuestions) && rawQuestions.length > 0) {
    list = rawQuestions.map((q, i) => ({
      ...q,
      questionId: q.questionId != null ? q.questionId : `q-${i}`,
    }));
  } else if (rawQuestions && typeof rawQuestions === 'object' && !Array.isArray(rawQuestions)) {
    list = Object.entries(rawQuestions).map(([id, q]) => ({
      ...q,
      questionId: q?.questionId != null ? q.questionId : id,
    }));
  }
  if (
    list.length === 0 &&
    Array.isArray(amcqQuestionsFlat) &&
    amcqQuestionsFlat.length > 0
  ) {
    list = amcqQuestionsFlat.map((q, i) => ({
      ...q,
      questionId: q?.questionId != null ? q.questionId : `flat-${i}`,
    }));
  }
  return list;
}

/**
 * @returns {Record<string, object>} questions suitable for SAQ create / loadDraft
 */
export function normalizeSaqQuestionsForExport(rawQuestions) {
  if (rawQuestions == null) return {};
  if (Array.isArray(rawQuestions)) {
    return rawQuestions.reduce((acc, q) => {
      const id = q?.questionId != null ? String(q.questionId) : uuidv4();
      acc[id] = { ...q, questionId: id };
      return acc;
    }, {});
  }
  if (typeof rawQuestions === 'object') {
    const out = {};
    Object.entries(rawQuestions).forEach(([key, q]) => {
      if (!q || typeof q !== 'object') return;
      const id = q.questionId != null ? String(q.questionId) : key;
      out[id] = { ...q, questionId: id };
    });
    return out;
  }
  return {};
}

export function isAmcqAssignmentData(assignmentData, assignmentId) {
  if (String(assignmentData?.format || '').toUpperCase() === 'AMCQ') return true;
  const suf =
    assignmentId && String(assignmentId).includes('+')
      ? String(assignmentId).split('+').pop()
      : '';
  return String(suf || '').toUpperCase() === 'AMCQ';
}

/**
 * Pure: build Firestore draft body (caller adds createdAt serverTimestamp).
 * @param {object} assignmentData — from assignments/{id}.data()
 * @param {string} targetClassId
 * @param {string} [assignmentId] — for draft id suffix fallback only
 */
export function buildExportedDraftDocument(assignmentData, targetClassId, assignmentId) {
  const format = exportFormatSuffix(assignmentData, assignmentId);
  const newDraftId = `${targetClassId}+${Date.now()}+${format}`;

  const questions = isAmcqAssignmentData(assignmentData, assignmentId)
    ? normalizeAmcqQuestionsForExport(
        assignmentData.questions,
        assignmentData.amcqQuestionsFlat
      )
    : normalizeSaqQuestionsForExport(assignmentData.questions);

  const draftData = {
    ...assignmentData,
    classId: targetClassId,
    selectedStudents: [],
    questions,
    format: assignmentData.format || format,
  };
  delete draftData.id;
  return { newDraftId, draftData };
}

/**
 * Writes drafts/{newDraftId} and updates classes/{targetClassId}.drafts.
 */
export async function exportAssignmentAsDraftToClass(db, assignmentId, targetClassId) {
  const assignmentRef = doc(db, 'assignments', assignmentId);
  const assignmentDoc = await getDoc(assignmentRef);
  if (!assignmentDoc.exists()) {
    throw new Error('Assignment not found');
  }
  const assignmentData = assignmentDoc.data();
  const { newDraftId, draftData } = buildExportedDraftDocument(
    assignmentData,
    targetClassId,
    assignmentId
  );

  const batch = writeBatch(db);
  const draftRef = doc(db, 'drafts', newDraftId);
  batch.set(draftRef, {
    ...draftData,
    createdAt: serverTimestamp(),
  });

  const classRef = doc(db, 'classes', targetClassId);
  batch.update(classRef, {
    drafts: arrayUnion({
      id: newDraftId,
      name: assignmentData.assignmentName || 'Untitled Assignment',
    }),
  });

  await batch.commit();
  return { newDraftId };
}
